import React, {useCallback} from 'react'
import type {
  UseMultipleSelectionStateChange,
  UseSelectStateChange,
} from 'downshift'
import CloseLineIcon from 'remixicon-react/CloseLineIcon'
import styled from 'styled-components'
import {palette} from 'styled-tools'
import {useRouter} from 'next/router'
import {isArray, mapValues, fromPairs} from 'lodash'
import qs from 'query-string'

import type {
  AttributeCountableConnection,
  AttributeValue,
} from 'src/api/saleor/generated'
import {toAttrOption} from 'src/utils/products'
import type {Maybe} from 'src/utils/Maybe'
import {isNotNull} from 'src/utils/isNotNull'
import {Select, MultiSelect} from '../form'
import {PSmall} from '../typography'

const Wrapper = styled.div`
  margin-bottom: 24px;
`

const CategoryBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${palette('black')};
  height: 50px;
  background-color: ${palette('white')};
`
const Filters = styled.div`
  display: flex;
  z-index: 99;
`

const SelectedFilters = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`

const Filter = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 10px;
  margin-right: 10px;
  background-color: ${palette('black')};
  color: ${palette('white')};

  ${PSmall} {
    margin-right: 10px;
    font-weight: 600;
  }

  svg {
    cursor: pointer;
  }
`

const Clear = styled.a`
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  border-bottom: 1px solid ${palette('black')};
  margin-left: 10px;
`

interface Props {
  filterableAttr: AttributeCountableConnection
}

interface Option {
  label: string
  value: string
}

const orderOptions: Option[] = [
  {value: 'new', label: 'Nýjast'},
  {value: 'price_desc', label: 'Verð (hæsta - lægsta)'},
  {value: 'price_asc', label: 'Verð (lægsta - hæsta)'},
  {value: 'alph_asc', label: 'Stafrófsröð (A - Ö)'},
  {value: 'alph_desc', label: 'Stafrófsröð (Ö - A)'},
]

const orderOptionsMap = fromPairs(orderOptions?.map((o) => [o.value, o]))

export default function CategoryFilterBar({
  filterableAttr,
}: Props): JSX.Element {
  const router = useRouter()

  const queryParams = mapValues(
    qs.parse(router.asPath.split('?')[1] ?? ''),
    (v) => {
      if (!v) {
        return null
      }
      return isArray(v) ? v : v.split(',')
    },
  )

  const attrOptions = toAttrOption(filterableAttr)

  const activeFilters = attrOptions
    ?.map((opt) =>
      opt?.attrValues?.filter((o) =>
        queryParams?.filters?.includes(o.slug as string),
      ),
    )
    .flat()

  const activeCategorySlug = router.query.slug as string | undefined

  const setSorting = useCallback(
    (sort: Maybe<string>) => {
      const params = {...queryParams, slug: activeCategorySlug}
      const search = qs.stringify({...params, sort}, {arrayFormat: 'comma'})
      router.replace({search}, undefined)
    },
    [queryParams, router, activeCategorySlug],
  )

  // TODO: Hver filter fær forskeytið filters í router query params, hvernig laga ég það?
  const updateFilters = useCallback(
    (filtersUpdate: Maybe<Maybe<AttributeValue>>[] = []) => {
      const filters = filtersUpdate.filter(isNotNull).map((f) => `${f.slug}`)

      const params = {...queryParams, slug: activeCategorySlug}
      const query = {...params, filters}

      router.replace({query}, undefined)
    },
    [queryParams, router, activeCategorySlug],
  )

  const handleSortChange = useCallback(
    (changes: UseSelectStateChange<Option>) => {
      setSorting(changes.selectedItem?.value)
    },
    [setSorting],
  )

  const handleFilterChange = useCallback(
    (changes: UseMultipleSelectionStateChange<AttributeValue>) => {
      updateFilters(changes.selectedItems)
    },
    [updateFilters],
  )

  function removeFilters(filter: AttributeValue): void {
    updateFilters(activeFilters.filter((b) => b !== filter))
  }

  function removeAll(): void {
    updateFilters([])
  }

  return (
    <Wrapper>
      <CategoryBar>
        <Filters>
          {attrOptions?.map((opt) => (
            <MultiSelect
              key={opt.id}
              placeholder={opt.attrName}
              items={opt.attrValues}
              selectedItems={activeFilters}
              handleSelectedItemsChange={handleFilterChange}
              itemToString={(option) => option?.name ?? ''}
              noBorder
            />
          ))}
        </Filters>
        <Filters>
          <Select
            placeholder="Raða"
            items={orderOptions}
            selectedItem={
              orderOptionsMap[queryParams.sort?.[0] || ''] || orderOptions[0]
            }
            handleSelectedItemChange={handleSortChange}
            itemToString={(option) => option?.label ?? ''}
            align="right"
            noBorder
          />
        </Filters>
      </CategoryBar>
      <SelectedFilters>
        {activeFilters?.map((filter) => (
          <Filter key={filter.id}>
            <PSmall>{filter.name}</PSmall>
            <CloseLineIcon size={14} onClick={() => removeFilters(filter)} />
          </Filter>
        ))}
        {activeFilters.length > 0 && (
          <Clear onClick={removeAll}>Hreinsa allt</Clear>
        )}
      </SelectedFilters>
    </Wrapper>
  )
}
